import React, { useState, useEffect } from 'react';
import { setPageMeta } from '../utils/setPageMeta';
import { ArrowLeft, Calendar, User, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactModal from '../components/ContactModal';

const BlogPost3 = () => {
  useEffect(() => {
    setPageMeta(
      'Trademark Registration: Why Your Brand Needs Protection - Legal Flick Blog',
      'Everything you need to know about trademark registration with the USPTO. Protect your brand with federal trademark registration and exclusive rights.'
    );
  }, []);

  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Blog Post Content */}
      <article className="flex-1 py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Button
            variant="ghost"
            className="mb-8"
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>

          {/* Article Header */}
          <header className="mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Trademark Registration: Why Your Brand Needs Protection
            </h1>
            
            <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-8">
              <div className="flex items-center">
                <User className="w-5 h-5 mr-2" />
                <span>Legal Team</span>
              </div>
              <div className="flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                <span>7 min read</span>
              </div>
            </div>

            <img
              src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1200&h=600&fit=crop"
              alt="Trademark Protection Guide"
              className="w-full h-[400px] object-cover rounded-lg shadow-lg"
            />
          </header>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Understanding Trademarks</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              A trademark is a word, phrase, symbol, design, or combination that identifies and distinguishes your goods or services from others. Federal trademark registration provides powerful legal protection for your brand identity and exclusive rights to use your mark in commerce.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Why Register Your Trademark?</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Federal trademark registration offers significant advantages:
            </p>
            <ul className="list-disc pl-6 mb-8 space-y-3 text-gray-700">
              <li><strong>Nationwide Protection:</strong> Exclusive rights to use your mark across all 50 states</li>
              <li><strong>Legal Presumption:</strong> Automatic legal presumption of ownership and right to use</li>
              <li><strong>Use the ® Symbol:</strong> Display the registered trademark symbol to establish credibility</li>
              <li><strong>Federal Court Access:</strong> Ability to sue infringers in federal court</li>
              <li><strong>Deterrent Effect:</strong> Prevents others from registering confusingly similar marks</li>
              <li><strong>Brand Value:</strong> Registered trademarks become valuable business assets</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">The Registration Process</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Registering a trademark with the USPTO involves several steps:
            </p>
            <ol className="list-decimal pl-6 mb-8 space-y-3 text-gray-700">
              <li><strong>Comprehensive Search:</strong> Search federal and state databases to check availability</li>
              <li><strong>Application Filing:</strong> Submit your trademark application with the USPTO</li>
              <li><strong>Examination:</strong> USPTO attorney reviews your application (3-4 months)</li>
              <li><strong>Publication:</strong> Your mark is published for opposition (30 days)</li>
              <li><strong>Registration:</strong> Certificate of registration issued (2-3 months)</li>
            </ol>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">What Can Be Trademarked?</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              You can trademark various elements of your brand:
            </p>
            <ul className="list-disc pl-6 mb-8 space-y-3 text-gray-700">
              <li>Brand names and product names</li>
              <li>Logos, symbols, and designs</li>
              <li>Slogans and taglines</li>
              <li>Product packaging and trade dress</li>
              <li>Sounds and jingles (in some cases)</li>
              <li>Colors used in specific contexts</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Maintaining Your Trademark</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              After registration, you must maintain your trademark:
            </p>
            <ul className="list-disc pl-6 mb-8 space-y-3 text-gray-700">
              <li>File Declaration of Use between years 5-6 after registration</li>
              <li>Renew your registration every 10 years</li>
              <li>Actively use your mark in commerce</li>
              <li>Monitor for potential infringement</li>
              <li>Take action against unauthorized use</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Common Trademark Mistakes</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Avoid these costly errors when protecting your brand:
            </p>
            <ul className="list-disc pl-6 mb-8 space-y-3 text-gray-700">
              <li>Not conducting a thorough search before filing</li>
              <li>Choosing a generic or descriptive mark</li>
              <li>Using your mark before securing rights</li>
              <li>Failing to monitor and enforce your trademark</li>
              <li>Not registering in appropriate classes</li>
            </ul>

            <div className="bg-blue-50 border-l-4 border-blue-600 p-6 my-12">
              <p className="text-gray-800 font-semibold mb-2">Ready to protect your brand?</p>
              <p className="text-gray-700 mb-4">Our trademark experts can guide you through the entire registration process.</p>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => setIsModalOpen(true)}
              >
                Get Started Today
              </Button>
            </div>
          </div>
        </div>
      </article>

      <Footer />
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default BlogPost3;